"use client";

import { useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  CheckPanel,
  KspPanel,
  MixPanel,
  type PanelStrings,
} from "@/components/tools/chem-reference/solubility-panels";
import { useToolUi } from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const SOLUBILITY_RULES_CHECKER_STRINGS = {
  modeLabel: "What do you want to find out?",
  modeCheck: "Is a compound soluble?",
  modeMix: "Does mixing two solutions make a precipitate?",
  modeKsp: "Ksp and molar solubility",
  results: "Results",
  formulaLabel: "Ionic compound",
  formulaHint:
    "Type a formula such as BaSO4, Pb(NO3)2 or (NH4)3PO4. Subscripts, brackets and hydrates (CaSO4·2H2O) are read.",
  formulaPlaceholder: "BaSO4",
  tryLabel: "Try an example",
  firstLabel: "First solution (a soluble salt)",
  secondLabel: "Second solution (a soluble salt)",
  concLabel: "Concentration of solution {n} (mol/L)",
  volumeLabel: "Volume of solution {n} (mL)",
  mixHint:
    "Optional: with the concentrations and volumes filled in, the tool also compares the ion product Q with Ksp for each precipitate. Clear a box to skip the check.",
  kspLabel: "Ksp (leave blank to use the table)",
  kspHint:
    "Scientific input such as 1.8e-10 works. Water of hydration is left out of Ksp.",
  cationInitLabel: "Cation already in solution (mol/L)",
  anionInitLabel: "Anion already in solution (mol/L)",
  commonHint:
    "A common ion from another source, such as chloride from NaCl for AgCl. Leave both blank for pure water.",
  digitsLabel: "Significant figures",
  emptyPrompt: "Enter a formula to begin.",
  formulaError:
    "That does not read as a chemical formula. Use element symbols with a capital first letter, numbers and brackets.",
  notSalt:
    "That is not a salt of the common ions this tool knows (Group 1 and 2 metals, NH₄⁺, Al³⁺, the common transition metals, Ag⁺, Pb²⁺, Hg₂²⁺; halides, nitrate, sulfate, carbonate, phosphate, hydroxide, sulfide, oxide and a few more).",
  sameCation:
    "Both solutions contain the same cation, so swapping partners gives the compounds you started with: no double displacement.",
  sameAnion:
    "Both solutions contain the same anion, so swapping partners gives the compounds you started with: no double displacement.",
  statusNames: {
    soluble: "Soluble",
    slightly: "Slightly soluble",
    insoluble: "Insoluble",
    reacts: "Reacts with water",
  },
  verdict: "Solubility of {formula}",
  ionsLabel: "Ions in the compound",
  ionsLine: "{n} {cation} and {m} {anion}",
  ruleLabel: "Rule applied",
  ruleText: {
    alkali:
      "Salts of Group 1 metals (Li⁺, Na⁺, K⁺, Rb⁺, Cs⁺) and of ammonium (NH₄⁺) are soluble.",
    solubleAnion:
      "Nitrates, chlorates, perchlorates, permanganates, acetates and hydrogen carbonates are soluble.",
    halide:
      "Chlorides, bromides and iodides are soluble, except those of Ag⁺, Hg₂²⁺, Cu⁺ and Pb²⁺ (PbCl₂ and PbBr₂ are only slightly soluble in cold water).",
    fluoride:
      "Fluorides are soluble, except those of Mg²⁺, Ca²⁺, Sr²⁺, Ba²⁺ and Pb²⁺.",
    sulfate:
      "Sulfates are soluble, except those of Ba²⁺, Sr²⁺ and Pb²⁺; sulfates of Ca²⁺, Ag⁺ and Hg₂²⁺ are slightly soluble.",
    hydroxide:
      "Hydroxides are insoluble, except those of Group 1, NH₄⁺, Ba²⁺ and Sr²⁺; Ca(OH)₂ is slightly soluble.",
    insolubleAnion:
      "Carbonates, phosphates, sulfites, chromates and oxalates are insoluble, except with Group 1 and NH₄⁺ (MgCrO₄ is soluble; CaCrO₄, SrCrO₄ and MgC₂O₄ are slightly soluble).",
    sulfide:
      "Sulfides are insoluble, except those of Group 1, NH₄⁺ and Group 2.",
    oxide:
      "Oxides are insoluble, except those of Group 1 and of Ca, Sr and Ba, which react with water to give the hydroxide.",
  },
  exceptionLine: "This compound is one of the named exceptions to that rule.",
  noteText: {
    alkaliException:
      "Lithium carbonate, lithium phosphate, lithium fluoride and potassium perchlorate are exceptions to the Group 1 rule: they are only slightly soluble.",
    hydrolysis:
      "Group 2 sulfides dissolve by reacting with water, so the solution holds HS⁻ and OH⁻ rather than S²⁻.",
    hydrogenCarbonate:
      "Hydrogen carbonates of the Group 2 metals exist only in solution; they turn into carbonates when dried.",
    hot: "More soluble in hot water than in cold.",
    none: "",
  },
  equilibriumLine: "A little dissolves: {equation}",
  kspExpression: "Ksp = {expression}",
  tabulatedKsp: "Tabulated Ksp of {formula} at 25 °C: {ksp}",
  tabulatedSolubility:
    "Molar solubility in pure water ≈ {molar} mol/L, or about {grams} g/L",
  noKsp:
    "No Ksp for this compound is in the table. Use the Ksp mode and enter one.",
  precipitates: "Precipitate",
  precipitateYes: "A precipitate forms: {names}.",
  precipitateNo: "No precipitate",
  productsHeading: "The two products",
  productLine: "{formula}: {status}",
  slightlyNote:
    "A slightly soluble product forms a precipitate only at high enough concentrations; the ion-product check below decides.",
  molecularLabel: "Molecular equation",
  completeLabel: "Complete ionic equation",
  netLabel: "Net ionic equation",
  noNet: "No net reaction: every ion stays in solution.",
  spectatorsLabel:
    "Ions that appear unchanged on both sides of the complete equation are spectators and are removed from the net equation.",
  qHeading: "Will it precipitate at these concentrations?",
  qLine:
    "{formula}: [cation] = {cation} mol/L, [anion] = {anion} mol/L after mixing, so Q = {q} against Ksp = {ksp}",
  qOver:
    "Q is greater than Ksp, so the solution is supersaturated and the solid forms.",
  qUnder:
    "Q is less than Ksp, so the solution is unsaturated and no solid forms.",
  qSaturated: "Q equals Ksp: the solution is exactly saturated.",
  qNoKsp: "No Ksp for {formula} is in the table, so Q cannot be compared.",
  qNeedValues:
    "Enter both concentrations and both volumes to compare Q with Ksp.",
  kspNeeded:
    "This compound is not in the Ksp table. Type its Ksp in the box to continue.",
  kspInvalid: "Enter a Ksp greater than zero, such as 1.8e-10.",
  concInvalid: "The ion concentrations must be numbers, or left blank.",
  kspUsed: "Ksp is the tabulated value at 25 °C.",
  kspCustom: "Ksp is the value you entered.",
  expressionLine: "{expression}: Ksp = {ksp}",
  molarWater: "Molar solubility in pure water",
  gramsWater: "Mass that dissolves in pure water",
  molarCommon: "Molar solubility with the ions present",
  gramsCommon: "Mass that dissolves with the ions present",
  reduction:
    "The common ion lowers the solubility by a factor of about {factor}.",
  alreadySaturated:
    "The ion product of what is already there (Q = {q}) is at least Ksp ({ksp}), so nothing more dissolves, and any solid present would not dissolve at all.",
  ionsAtEquilibrium: "Ion concentrations at equilibrium",
  initialQ: "Initial ion product",
  working: "The working",
  workWater:
    "s = (Ksp ÷ ({m}^{m} × {n}^{n}))^(1/{total}) = ({ksp} ÷ {denominator})^(1/{total}) = {s} mol/L",
  workCommon:
    "With the ions already present: [cation] = {cation}, [anion] = {anion}, so s = {s} mol/L (solved exactly, not approximated)",
  tableHeading:
    "Tabulated Ksp values and molar solubilities in pure water at 25 °C",
  columnCompound: "Compound",
  columnKsp: "Ksp",
  columnMolar: "Solubility (mol/L)",
  columnGrams: "Solubility (g/L)",
  sourceNote:
    "Rules: as taught in general chemistry (for example OpenStax Chemistry 2e, section 4.2). Ksp values: University of Wisconsin–Madison general-chemistry table, after R. J. Myers, J. Chem. Educ. 1986, 63, 687; other handbooks differ.",
} as const;

type Mode = "check" | "mix" | "ksp";

export default function SolubilityRulesCheckerWidget() {
  const strings = useToolUi(SOLUBILITY_RULES_CHECKER_STRINGS);
  const [mode, setMode] = useState<Mode>("check");
  const panel = strings as unknown as PanelStrings;
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="sr-mode"
        onChange={setMode}
        options={[
          { value: "check", label: strings.modeCheck },
          { value: "mix", label: strings.modeMix },
          { value: "ksp", label: strings.modeKsp },
        ]}
        value={mode}
      />
      {mode === "check" ? <CheckPanel strings={panel} /> : null}
      {mode === "mix" ? <MixPanel strings={panel} /> : null}
      {mode === "ksp" ? <KspPanel strings={panel} /> : null}
      <p className="text-muted-foreground text-xs">{strings.sourceNote}</p>
    </>
  );
}
